import { z } from 'zod';
import { isoDate, uuid } from './common';
import { knowledgeEntry } from './knowledge';

/**
 * Topics (PRD 30 §3): meetings that were really one discussion. The app
 * proposes; a person says "write it up" or "not one thing"; the write-up is
 * the record a company would keep — what was decided, what was rejected
 * and why, what is still open — and its statements replace the per-meeting
 * ones so intermediate states never survive.
 */
export const topicStatus = z.enum(['proposed', 'open', 'wrapped', 'dismissed']);
export type DiscussionStatus = z.infer<typeof topicStatus>;

export const topicMeeting = z.object({
  id: uuid,
  title: z.string(),
  startedAt: isoDate,
  seconds: z.number().int(),
});

export const topic = z.object({
  id: uuid,
  title: z.string(),
  status: topicStatus,
  spaceId: uuid,
  startedAt: isoDate,
  endedAt: isoDate,
  seconds: z.number().int(),
  meetings: z.array(topicMeeting),
  wroteUpAt: isoDate.nullable(),
  entryCount: z.number().int(),
  /** Whether THIS reader may write it up or dismiss it: they are in its space. */
  canAct: z.boolean(),
});
export type Topic = z.infer<typeof topic>;

export const discussionDetail = topic.extend({
  writeUp: z.string().nullable(),
  entries: z.array(knowledgeEntry),
});
export type DiscussionDetail = z.infer<typeof discussionDetail>;

export const discussionsList = z.object({
  proposed: z.array(topic),
  discussions: z.array(topic),
});
export type DiscussionsList = z.infer<typeof discussionsList>;

/** Make one by hand: two to eight meetings this person can open. */
export const createDiscussionBody = z.object({
  meetingIds: z.array(uuid).min(2).max(8),
  title: z.string().trim().min(2).max(120).optional(),
});
export type CreateDiscussionBody = z.infer<typeof createDiscussionBody>;
